import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useHandler } from "@/hooks";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import {
  useActiveVersionNumber,
  useParamVersionNumber,
} from "@/lib/hooks/useActiveVersionNumber";
import { useLatestVersionNumber } from "@/lib/hooks/useVersionInformation";
import { useMessageStore } from "@/state/stores";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDownIcon } from "lucide-react";
import { useShallow } from "zustand/shallow";
import { VersionItem } from "./VersionItem";

export function VersionDropdown() {
  const versionNumber = useActiveVersionNumber();
  const paramVersionNumber = useParamVersionNumber();
  const navigate = useNavigate();
  const chatId = useActiveChatId();
  const onVersionSelect = useHandler((version: number) => {
    if (version === versionNumber) {
      return;
    }

    // If there's no version number in the URL params, we need to navigate to
    // the spec page (which is the default page/tab).
    if (!paramVersionNumber) {
      if (!chatId) {
        // This should never happen, but we'll handle it just in case.
        console.error("No chat ID available");
        return;
      }

      navigate({
        to: "/codegen/$chatId/attachments/$versionNumber",
        params: {
          chatId,
          versionNumber: `v${version}`,
        },
        search: { tab: "spec" },
      });
      return;
    }

    navigate({
      to: ".",
      params: {
        versionNumber: `v${version}`,
      },
    });
  });

  const versions = useMessageStore(
    useShallow((state) =>
      Object.values(state.versionInformation)
        .map((v) => v.version)
        .sort((a, b) => a - b),
    ),
  );
  const latestVersion = useLatestVersionNumber();

  return (
    <FpDropdownMenu>
      <FpDropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm">
          v{versionNumber}
          <ChevronDownIcon className="w-4 h-4 ml-1" />
        </Button>
      </FpDropdownMenuTrigger>
      <FpDropdownMenuContent align="end">
        {versions.map((version) => (
          <FpDropdownMenuItem
            key={version}
            onClick={() => onVersionSelect(version)}
            className="hover:bg-secondary hover:text-secondary-foreground focus:bg-secondary focus:text-secondary-foreground cursor-pointer"
          >
            <VersionItem
              version={version}
              isActive={version === versionNumber}
              isLatest={version === latestVersion}
            />
          </FpDropdownMenuItem>
        ))}
      </FpDropdownMenuContent>
    </FpDropdownMenu>
  );
}
